import * as React from 'react';
import { Button, Card, CardContent, Typography, Grid, TextField as MuiTextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  SimpleList,
  List,
  Datagrid,
  ImageField,
  TextField,
  ShowButton,
  EditButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  Create,
  SimpleForm,
  TextInput,
  Edit,
  ImageInput,
  FunctionField,
  Show,
  SimpleShowLayout
} from 'react-admin';

import ImgField from '../components/ImgField'
import ListFilter from '../components/ListFilter'
import { httpClient } from '../utils/dataProvider'
import { PageHeader, Surface, DetailGrid, DetailItem, StatusPill, ActionButton, Avatar, Row, RowTitle, RowMeta, RowText } from '../components/ui';

const ALL_FILTERS = ['active', 'banned']

export const UserServicesList = props => {
  const [userservices, setUserServices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState('ALL');
  const [userFilter, setUserFilter] = React.useState('ALL');
  const [serviceFilter, setServiceFilter] = React.useState('ALL');
  const [streetFilter, setStreetFilter] = React.useState('ALL');
  const [cityFilter, setCityFilter] = React.useState('ALL');
  const [countryFilter, setCountryFilter] = React.useState('ALL');

  // Helper to parse the locationString into parts
  const parseLocation = React.useCallback((locationString = '') => {
    if (!locationString || typeof locationString !== 'string') {
      return { street: 'Unknown Street', city: locationString || '', country: 'Unknown Country' };
    }
    const parts = locationString.split(',');
    const country = parts.length > 0 ? parts[parts.length - 1].trim() : 'Unknown Country';
    const city = parts.length > 1 ? parts[parts.length - 2].trim() : locationString;
    const street = parts.length > 2 ? parts[parts.length - 3].trim() : 'Unknown Street';
    return { street, city, country };
  }, []);

  const load = async () => {
    setLoading(true);
    httpClient('https://skilzapp.net/api/userservices').then(({ json }) => {
      setUserServices(json.docs);
      //setCountries([...new Set(json.docs.map(user => user.country).filter(Boolean))])
      //setCities([...new Set(json.docs.map(user => user.city).filter(Boolean))])
    }).finally(() => {
      setLoading(false);
    });
  };

  React.useEffect(() => {
    load();
  }, []);

  // Dynamic option lists
  const userOptions = React.useMemo(() => {
    const map = {};
    userservices.forEach(r => {
      const u = r?.createdBy;
      if (u?._id) map[u._id] = u;
    });
    return Object.values(map).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [userservices]);

  const serviceOptions = React.useMemo(() => {
    const map = {};
    userservices.forEach(r => {
      const svc = r?.defaultService;
      if (svc?._id) map[svc._id] = svc;
    });
    return Object.values(map).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [userservices]);

  // Location options using the same parsing logic
  const countryOptions = React.useMemo(() => {
    const countries = new Set();
    userservices.forEach(service => {
      const { country } = parseLocation(service.locationString);
      countries.add(country);
    });
    return Array.from(countries).sort();
  }, [userservices, parseLocation]);

  const cityOptions = React.useMemo(() => {
    const cities = new Set();
    userservices.forEach(service => {
      const { city, country } = parseLocation(service.locationString);
      if (countryFilter !== 'ALL' && country !== countryFilter) return;
      cities.add(city);
    });
    return Array.from(cities).sort();
  }, [userservices, parseLocation, countryFilter]);

  const streetOptions = React.useMemo(() => {
    const streets = new Set();
    userservices.forEach(service => {
      const { street, city, country } = parseLocation(service.locationString);
      if (countryFilter !== 'ALL' && country !== countryFilter) return;
      if (cityFilter !== 'ALL' && city !== cityFilter) return;
      streets.add(street);
    });
    return Array.from(streets).sort();
  }, [userservices, parseLocation, countryFilter, cityFilter]);

  // Apply user & service filters first
  const baseFiltered = React.useMemo(() => {
    return userservices.filter(r => {
      if (userFilter !== 'ALL') {
        const uID = r?.createdBy?._id;
        if (uID !== userFilter) return false;
      }
      if (serviceFilter !== 'ALL') {
        if (r?.defaultService?._id !== serviceFilter) return false;
      }

      // Apply location filters
      if (streetFilter !== 'ALL' || cityFilter !== 'ALL' || countryFilter !== 'ALL') {
        const { street, city, country } = parseLocation(r.locationString);
        if (streetFilter !== 'ALL' && street !== streetFilter) return false;
        if (cityFilter !== 'ALL' && city !== cityFilter) return false;
        if (countryFilter !== 'ALL' && country !== countryFilter) return false;
      }

      return true;
    });
  }, [userservices, userFilter, serviceFilter, streetFilter, cityFilter, countryFilter, parseLocation]);

  const filteredServices = filter === 'ALL' ? baseFiltered : baseFiltered.filter(r => filter === 'active' ? r.status !== 'banned' : r.status === filter);

  return (
    <main className="sk-page sk-user-services">
      <PageHeader eyebrow="Marketplace" title="Registered Services" description="Review provider listings, service coverage, and registration status." />
      <Grid container spacing={2} style={{ marginBottom: '16px' }}>
        <Grid item xs={6}>
          <Autocomplete
            size="small"
            value={userFilter === 'ALL' ? null : userOptions.find(u => u._id === userFilter) || null}
            onChange={(e, val) => setUserFilter(val ? val._id : 'ALL')}
            options={userOptions}
            getOptionLabel={(o) => o?.name ? `${o.name}${o.phone ? ' (' + o.phone + ')' : ''}` : ''}
            renderInput={(params) => <MuiTextField {...params} label={`User (${userOptions.length})`} variant="outlined" />}
            clearOnEscape
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            size="small"
            value={serviceFilter === 'ALL' ? null : serviceOptions.find(s => s._id === serviceFilter) || null}
            onChange={(e, val) => setServiceFilter(val ? val._id : 'ALL')}
            options={serviceOptions}
            getOptionLabel={(o) => o?.name || ''}
            renderInput={(params) => <MuiTextField {...params} label={`Service (${serviceOptions.length})`} variant="outlined" />}
            clearOnEscape
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginBottom: '16px' }}>
        <Grid item xs={4}>
          <Autocomplete
            size="small"
            value={countryFilter === 'ALL' ? null : countryFilter}
            onChange={(e, val) => {
              // When country changes/clears, clear city & street
              const newCountry = val || 'ALL';
              setCountryFilter(newCountry);
              setCityFilter('ALL');
              setStreetFilter('ALL');
            }}
            options={countryOptions}
            getOptionLabel={(o) => o || ''}
            renderInput={(params) => <MuiTextField {...params} label={`Country (${countryOptions.length})`} variant="outlined" />}
            clearOnEscape
          />
        </Grid>

        <Grid item xs={4}>
          <Autocomplete
            size="small"
            value={cityFilter === 'ALL' ? null : cityFilter}
            onChange={(e, val) => {
              if (!val) {
                // Clearing city should only clear street
                setCityFilter('ALL');
                setStreetFilter('ALL');
                return;
              }
              // Selecting a city should auto-select the country
              const selectedCity = val;
              // Find a matching country for the chosen city (prefer within current country if set)
              let matchedCountry = countryFilter !== 'ALL' ? countryFilter : null;
              if (!matchedCountry) {
                for (const svc of userservices) {
                  const { city, country } = parseLocation(svc.locationString);
                  if (city === selectedCity) {
                    matchedCountry = country;
                    break;
                  }
                }
              }
              setCountryFilter(matchedCountry || 'ALL');
              setCityFilter(selectedCity);
              // Changing city should clear street
              setStreetFilter('ALL');
            }}
            options={cityOptions}
            getOptionLabel={(o) => o || ''}
            renderInput={(params) => <MuiTextField {...params} label={`City (${cityOptions.length})`} variant="outlined" />}
            clearOnEscape
          />
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            size="small"
            value={streetFilter === 'ALL' ? null : streetFilter}
            onChange={(e, val) => {
              if (!val) {
                setStreetFilter('ALL');
                return;
              }
              const selectedStreet = val;
              // Auto-select city & country based on the chosen street
              // Prefer matches that are consistent with already selected city/country
              let matchedCity = cityFilter !== 'ALL' ? cityFilter : null;
              let matchedCountry = countryFilter !== 'ALL' ? countryFilter : null;
              for (const svc of userservices) {
                const { street, city, country } = parseLocation(svc.locationString);
                if (street === selectedStreet) {
                  if (matchedCity && city !== matchedCity) continue;
                  if (matchedCountry && country !== matchedCountry) continue;
                  matchedCity = matchedCity || city;
                  matchedCountry = matchedCountry || country;
                  break;
                }
              }
              if (matchedCountry) setCountryFilter(matchedCountry);
              if (matchedCity) setCityFilter(matchedCity);
              setStreetFilter(selectedStreet);
            }}
            options={streetOptions}
            getOptionLabel={(o) => o || ''}
            renderInput={(params) => <MuiTextField {...params} label={`Street (${streetOptions.length})`} variant="outlined" />}
            clearOnEscape
          />
        </Grid>
      </Grid>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button variant={filter === 'ALL' ? "contained" : "outlined"} color="primary" onClick={() => setFilter('ALL')}>All ({baseFiltered.length})</Button>
        {ALL_FILTERS.map(status => (
          <Button key={status} variant={filter === status ? "contained" : "outlined"} color="primary" onClick={() => setFilter(status)}>
            {status} ({baseFiltered.filter(r => status === 'active' ? r.status !== 'banned' : r.status === status).length})
          </Button>
        ))}
      </div>
      <Card style={{ flex: 1 }}>
        <CardContent>
          {loading && <p>Loading...</p>}
          {filteredServices
            .map((service, i) => {
              const locations = service.locationString?.split(',') || [];

              const country = locations.length > 0 ? locations[locations.length - 1].trim() : 'Unknown Country';
              const city = locations.length > 1 ? locations[locations.length - 2].trim() : service.locationString;

              const street = locations.length > 2 ? locations[locations.length - 3].trim() : 'Unknown Street';

              return (
                <Row key={i} right={<StatusPill status={service.status || 'active'} />}>
                  <Avatar alt={service.defaultService.name} />
                  <RowText>
                    <RowTitle href={`/#/userservices/${service._id}/show`}>{service.defaultService.name}</RowTitle>
                    <RowMeta>{service.createdBy.name} — {service.createdBy.phone}</RowMeta>
                    <RowMeta dim>
                      {street}, {city}, {country}
                      {' · '}
                      <a href={`https://www.google.com/maps?q=${service.location.coordinates[1]},${service.location.coordinates[0]}`} target="_blank" rel="noopener noreferrer">View on map</a>
                    </RowMeta>
                    <RowMeta dim>{new Date(service.createdAt).toLocaleString()}</RowMeta>
                  </RowText>
                </Row>
              )
            })}
        </CardContent>
      </Card>
    </main>
  );
};


export const UserServicesShow = (props) => (
  <Show {...props} className="sk-ra-page sk-detail-page">
    <FunctionField render={record => {
      if (!record) return null;
      const user = record.createdBy || {};
      const svc = record.defaultService || {};
      return (
        <div className="sk-page">
          <header className="sk-profile-band">
            <div className="sk-profile-identity">
              <Avatar alt={svc.name} size="lg" />
              <div>
                <h1>{svc.name}</h1>
                <p>Registered by {user.name || 'Unknown'}</p>
              </div>
            </div>
            <div className="sk-profile-actions">
              <BanButton status={record.status} userServiceId={record.id} />
            </div>
          </header>

          <Surface title="Service Details">
            <DetailGrid>
              <DetailItem label="Provider" value={user.name} />
              <DetailItem label="Phone" value={user.phone} />
              <DetailItem label="Service" value={svc.name} />
              <DetailItem label="Status" value={record.status} />
              <DetailItem label="Location" value={record.locationString} wide />
              {record.desc && <DetailItem label="Description" value={record.desc} wide />}
            </DetailGrid>
          </Surface>

          {record.location && record.location.coordinates && (
            <Surface title="Location">
              <a
                href={`https://www.google.com/maps?q=${record.location.coordinates[1]},${record.location.coordinates[0]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="sk-link-chip"
              >
                View on Google Maps ↗
              </a>
            </Surface>
          )}

          {/* Service Mode & Credentials */}
          <Surface title="Registration & Credentials">
            <DetailGrid>
              <DetailItem label="Service Mode" value={record.serviceMode || 'Not set'} />
              <DetailItem label="Tax ID" value={record.taxIdNumber || '—'} />
              <DetailItem label="Credentials Verified" value={record.credentialsVerified ? '✅ Yes' : '❌ No'} />
              {record.credentialsDeadline && (
                <DetailItem label="Deadline" value={new Date(record.credentialsDeadline).toLocaleDateString()} />
              )}
            </DetailGrid>

            {record.credentials && record.credentials.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.6px', color: '#566071', marginBottom: 8 }}>
                  Uploaded Credentials ({record.credentials.length})
                </div>
                {record.credentials.map((cred, i) => (
                  <Row key={cred._id || i}>
                    <RowText>
                      <RowTitle>{cred.label || cred.type}</RowTitle>
                      <RowMeta>
                        Type: {cred.type}
                        {cred.fileURL && (
                          <> · <a href={`https://skilzcdn.nyc3.cdn.digitaloceanspaces.com/${cred.fileURL}`} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>View ↗</a></>
                        )}
                      </RowMeta>
                      <RowMeta dim>
                        {cred.uploadedAt && `Uploaded ${new Date(cred.uploadedAt).toLocaleDateString()}`}
                        {cred.reviewedAt && ` · Reviewed ${new Date(cred.reviewedAt).toLocaleDateString()}`}
                        {cred.rejectionReason && ` · Reason: ${cred.rejectionReason}`}
                      </RowMeta>
                    </RowText>
                    <StatusPill status={
                      cred.reviewStatus === 'approved' ? 'active' :
                      cred.reviewStatus === 'rejected' ? 'banned' : 'pending'
                    }>
                      {cred.reviewStatus || 'pending'}
                    </StatusPill>
                  </Row>
                ))}
              </div>
            )}
            {(!record.credentials || record.credentials.length === 0) && (
              <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>
                No credentials uploaded yet.
              </div>
            )}
          </Surface>
        </div>
      );
    }} />
  </Show>
);

const BanButton = props => {
  const [loading, setLoading] = React.useState(false);
  const { status, userServiceId } = props;
  const handleDelete = () => {
    setLoading(true);
    httpClient(`https://skilzapp.net/api/userservices/setBanStatus/${userServiceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: status === 'banned' ? 'active' : 'banned' }),
    }).then(() => {
      window.location.reload()
    }).catch(error => {
      console.error(error);
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <StatusPill status={status === 'banned' ? 'banned' : 'active'}>
        {status === 'banned' ? 'Banned' : 'Active'}
      </StatusPill>
      <ActionButton
        variant={status === 'banned' ? 'secondary' : 'danger'}
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? 'Processing…' : status === 'banned' ? 'Remove Ban' : 'Ban Service'}
      </ActionButton>
    </div>
  );
}